
import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { RouteData } from './useRouting';

interface UseMapInitializationProps {
  mapboxToken: string | null;
  containerId: string;
  from?: string;
  to?: string;
  /**
   * Where to open the map before a route has been drawn. Pass the pickup when
   * it is known: the fallback below is a whole-country view, which is honest
   * about knowing nothing, where the previous default was a specific point in
   * New Jersey that had nothing to do with anybody's trip.
   */
  initialCenter?: [number, number];
}

/** Roughly the geographic centre of the contiguous United States. */
const US_CENTER: [number, number] = [-98.5, 39.8];

export const useMapInitialization = ({
  mapboxToken,
  containerId,
  from,
  to,
  initialCenter,
}: UseMapInitializationProps) => {
  const map = useRef<mapboxgl.Map | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);

  // Read through a ref: the map is created once, and listing the centre in the
  // effect's dependencies would tear down and rebuild it whenever the caller
  // re-rendered with a fresh array.
  const initialCenterRef = useRef(initialCenter);
  initialCenterRef.current = initialCenter;

  // Initialize Mapbox map
  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container || !mapboxToken) return;

    try {
      setIsLoading(true);
      setError(null);
      
      // Initialize Mapbox
      mapboxgl.accessToken = mapboxToken;
      
      // Create map instance
      if (!map.current) {
        map.current = new mapboxgl.Map({
          container,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: initialCenterRef.current ?? US_CENTER,
          zoom: initialCenterRef.current ? 12 : 3
        });
        
        // Add navigation controls
        map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      }

      // Wait for the map to load
      map.current.on('load', () => {
        setIsMapReady(true);
        setIsLoading(false);
      });

    } catch (err) {
      console.error('Error initializing map:', err);
      setError('Failed to load map. Please check your Mapbox token and try again.');
      setIsLoading(false);
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [mapboxToken, containerId]);

  // Add a marker to the map
  const addMarkerToMap = (
    coords: [number, number],
    color: string,
    title: string
  ) => {
    if (!map.current) return;

    const markerEl = document.createElement('div');
    markerEl.style.width = '16px';
    markerEl.style.height = '16px';
    markerEl.style.borderRadius = '50%';
    markerEl.style.backgroundColor = color;
    markerEl.style.border = '2px solid white';
    markerEl.style.boxShadow = '0 0 4px rgba(0,0,0,0.5)';
    
    new mapboxgl.Marker(markerEl)
      .setLngLat(coords)
      .setPopup(new mapboxgl.Popup().setText(title))
      .addTo(map.current);
  };

  // Fit the map to show both points
  const fitBounds = (fromCoords: [number, number], toCoords: [number, number], padding: number = 50) => {
    if (!map.current) return;
    
    const bounds = new mapboxgl.LngLatBounds()
      .extend(fromCoords)
      .extend(toCoords);
    
    map.current.fitBounds(bounds, {
      padding,
      maxZoom: 15
    });
  };

  return {
    map: map.current,
    isLoading,
    isMapReady,
    error,
    addMarkerToMap,
    fitBounds
  };
};
